import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useSSEStore } from '../../stores/sseStore';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { BagBadge } from '../../components/BagBadge';
import { useCan } from '../../lib/rbac';
import {
  AlertTriangle, RefreshCw, ChevronLeft, ChevronRight,
  MessageCircle, DollarSign, Shield, Clock, Send,
  ArrowUpRight, X, ChevronDown, ChevronUp, Package
} from 'lucide-react';

// ── Types ──
interface ClaimPlayer {
  role: string;
  type: string;
  user_id: number;
  available_actions: { action: string; mandatory: boolean; due_date: string | null }[];
}

interface ClaimDetail {
  due_date: string | null;
  action_responsible: string;
  title: string;
  description: string;
  problem: string;
}

interface OrderContext {
  ml_order_id: number;
  buyer_nickname: string | null;
  paid_amount: number | null;
  context_site: string | null;
  order_status: string | null;
  item_id: string | null;
  item_title: string | null;
  asin: string | null;
  amazon_cost: number | null;
  amazon_status: string | null;
  amazon_tracking: string | null;
  shipment_status: string | null;
  shipment_tracking: string | null;
  estimated_delivery: string | null;
}

interface EnrichedClaim {
  id: number;
  resource_id: number;
  status: string;
  type: string;
  stage: string;
  resource: string;
  reason_id: string;
  fulfilled: boolean;
  quantity_type: string;
  players: ClaimPlayer[];
  resolution: { reason: string; date_created: string; benefited: string; closed_by: string } | null;
  site_id: string;
  date_created: string;
  last_updated: string;
  detail: ClaimDetail | null;
  order_context: OrderContext | null;
  profit: number | null;
  margin_pct: number | null;
}

interface ClaimFullDetail extends EnrichedClaim {
  messages: { sender_role: string; receiver_role: string; message: string; translated_message: string | null; date_created: string; stage: string }[];
  actions_history: { action_name: string; player_role: string; claim_stage: string; date_created: string }[];
  status_history: { stage: string; status: string; date: string; change_by: string }[];
  expected_resolutions: unknown[];
  affects_reputation: { affects_reputation: string; has_incentive: boolean; due_date: string | null } | null;
  partial_refund_offers: { currency_id: string; available_offers: { amount: number; percentage: number }[] } | null;
}

interface ClaimStats {
  total: number;
  open_claims: number;
  closed_claims: number;
  lost: number;
  won: number;
}

const SITE_FLAGS: Record<string, string> = { MLA: '🇦🇷', MLM: '🇲🇽', MLC: '🇨🇱', MCO: '🇨🇴', MLU: '🇺🇾', MLB: '🇧🇷' };

/** Strip HTML tags and decode entities for plain text display */
function stripHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent?.trim() || '';
}

/** Check if string contains HTML tags */
function isHtml(str: string): boolean {
  return /<[a-z][\s\S]*>/i.test(str);
}
const fmt = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const REASON_LABELS: Record<string, string> = {
  PNR: 'No Recibido',
  PDD: 'Defectuoso/Incorrecto',
  CS: 'Cancelado',
};

function getReasonType(reasonId: string): string {
  const prefix = reasonId?.substring(0, 3) || '';
  return REASON_LABELS[prefix] || prefix;
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const hours = Math.floor(diff / 3600000);
  if (hours < 1) return `${Math.floor(diff / 60000)}m`;
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

export default function ClaimsTab({ sellerId }: { sellerId: number }) {
  const { t } = useTranslation();
  const [claims, setClaims] = useState<EnrichedClaim[]>([]);
  const [stats, setStats] = useState<ClaimStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<'opened' | 'closed'>('opened');
  const [selectedClaim, setSelectedClaim] = useState<ClaimFullDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadClaims = useCallback(async () => {
    setLoading(true);
    const res = await api.get<EnrichedClaim[]>(`/mercadolibre/claims/open?seller_id=${sellerId}&status=${filter}&limit=50`);
    if (res.success && res.data) {
      setClaims(res.data);
    }
    setLoading(false);
  }, [sellerId, filter]);

  const loadStats = useCallback(async () => {
    const res = await api.get<ClaimStats>(`/mercadolibre/claims/stats?seller_id=${sellerId}`);
    if (res.success && res.data) setStats(res.data);
  }, [sellerId]);

  useEffect(() => { loadClaims(); loadStats(); }, [loadClaims, loadStats]);

  // SSE: refresh on claim events
  const { subscribe } = useSSEStore();
  const claimsRef = useRef(loadClaims);
  const statsRef = useRef(loadStats);
  claimsRef.current = loadClaims;
  statsRef.current = loadStats;
  useEffect(() => {
    const refresh = () => { claimsRef.current(); statsRef.current(); };
    const unsubs = [
      subscribe('claim:message', refresh),
      subscribe('claim:refunded', refresh),
      subscribe('claim:partial-refund', refresh),
      subscribe('claim:return', refresh),
      subscribe('claim:return-review', refresh),
      subscribe('claim:dispute', refresh),
    ];
    return () => unsubs.forEach(u => u());
  }, [subscribe]);

  const openDetail = async (claimId: number) => {
    setDetailLoading(true);
    const res = await api.get<ClaimFullDetail>(`/mercadolibre/claims/${claimId}?seller_id=${sellerId}`);
    if (res.success && res.data) setSelectedClaim(res.data);
    setDetailLoading(false);
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const autoOpenedRef = useRef<string | null>(null);
  useEffect(() => {
    const claimParam = searchParams.get('claim');
    if (!claimParam) return;
    const key = `${sellerId}:${claimParam}`;
    if (autoOpenedRef.current === key) return;
    const cid = parseInt(claimParam, 10);
    if (Number.isNaN(cid) || cid <= 0) return;
    autoOpenedRef.current = key;
    openDetail(cid).finally(() => {
      const next = new URLSearchParams(searchParams);
      next.delete('claim');
      setSearchParams(next, { replace: true });
    });
  }, [searchParams, sellerId]);

  return (
    <div className="space-y-4">
      {/* Stats bar */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <StatCard label={t('ml.c.openClaims')} value={stats.open_claims} color="text-yellow-400" />
          <StatCard label={t('ml.claimClosed')} value={stats.closed_claims} color="text-gray-400" />
          <StatCard label={t('ml.claimWon')} value={stats.won} color="text-green-400" />
          <StatCard label={t('ml.claimLost')} value={stats.lost} color="text-red-400" />
          <StatCard label={t('common.total')} value={stats.total} color="text-[var(--color-text)]" />
        </div>
      )}

      {/* Controls */}
      <div className="flex items-center gap-3">
        <div className="flex bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg overflow-hidden">
          {(['opened', 'closed'] as const).map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                filter === f
                  ? 'bg-[var(--color-primary)] text-white'
                  : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
              }`}
            >
              {f === 'opened' ? t('ml.claimOpen') : t('ml.claimClosed')}
            </button>
          ))}
        </div>
        <button
          onClick={() => { loadClaims(); loadStats(); }}
          className="ml-auto p-2 text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
          title={t('ml.c.refresh')}
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Claims list */}
      {loading ? (
        <div className="p-8 text-center text-[var(--color-text-muted)]">{t('common.loading')}</div>
      ) : claims.length === 0 ? (
        <div className="p-8 text-center text-[var(--color-text-muted)]">{t('ml.c.noClaims')}</div>
      ) : (
        <div className="space-y-3">
          {claims.map(claim => (
            <ClaimCard
              key={claim.id}
              claim={claim}
              t={t}
              onClick={() => openDetail(claim.id)}
            />
          ))}
        </div>
      )}

      {/* Detail panel */}
      {(selectedClaim || detailLoading) && (
        <ClaimDetailPanel
          claim={selectedClaim}
          loading={detailLoading}
          sellerId={sellerId}
          onClose={() => setSelectedClaim(null)}
          onRefresh={() => selectedClaim && openDetail(selectedClaim.id)}
          t={t}
        />
      )}
    </div>
  );
}

// ── Stat card ──
function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg p-3 text-center">
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
      <div className="text-xs text-[var(--color-text-muted)]">{label}</div>
    </div>
  );
}

// ── Claim card ──
function ClaimCard({ claim, t, onClick }: { claim: EnrichedClaim; t: (k: string) => string; onClick: () => void }) {
  const canMoney = useCan()('global.view_money');
  const sellerPlayer = claim.players?.find(p => p.role === 'respondent');
  const hasMandatory = sellerPlayer?.available_actions?.some(a => a.mandatory);
  const dueDate = sellerPlayer?.available_actions?.find(a => a.mandatory)?.due_date;
  const reasonType = getReasonType(claim.reason_id);
  const reasonKey = claim.reason_id.substring(0, 3).toLowerCase();

  const stageColors: Record<string, string> = {
    claim: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
    dispute: 'bg-red-500/10 text-red-400 border-red-500/20',
    recontact: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  };

  return (
    <div
      onClick={onClick}
      className={`bg-[var(--color-bg-secondary)] border rounded-lg p-4 cursor-pointer hover:border-[var(--color-primary)] transition-all ${
        hasMandatory ? 'border-red-500/50 shadow-[0_0_8px_rgba(239,68,68,0.15)]' : 'border-[var(--color-border)]'
      }`}
    >
      <div className="flex items-start gap-4">
        {/* Left: reason icon + site */}
        <div className="flex flex-col items-center gap-1 min-w-[50px]">
          <span className="text-2xl">{SITE_FLAGS[claim.site_id] || '🌐'}</span>
          <span className={`text-xs px-2 py-0.5 rounded-full border ${stageColors[claim.stage] || 'bg-gray-500/10 text-gray-400 border-gray-500/20'}`}>
            {t(`ml.c.${claim.stage}`) || claim.stage}
          </span>
        </div>

        {/* Center: info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-xs text-[var(--color-text-muted)]">#{claim.id}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              reasonKey === 'pnr' ? 'bg-orange-500/10 text-orange-400' :
              reasonKey === 'pdd' ? 'bg-purple-500/10 text-purple-400' :
              'bg-gray-500/10 text-gray-400'
            }`}>
              {t(`ml.c.${reasonKey}`) || reasonType}
            </span>
            {claim.detail?.action_responsible && (
              <span className="text-xs text-[var(--color-text-muted)]">
                → {t(`ml.c.${claim.detail.action_responsible}`) || claim.detail.action_responsible}
              </span>
            )}
          </div>

          <div className="text-sm text-[var(--color-text)] mb-1">
            {claim.detail?.title || claim.detail?.problem || claim.reason_id}
          </div>

          {claim.order_context && (
            <div className="flex flex-wrap items-center gap-3 text-xs text-[var(--color-text-muted)]">
              <span>{t('ml.c.buyer')}: <span className="text-[var(--color-text)]">{claim.order_context.buyer_nickname}</span></span>
              {claim.order_context.item_title && (
                <span title={claim.order_context.item_title}>{claim.order_context.item_title}</span>
              )}
              {claim.order_context.asin && (
                <span className="font-mono text-[var(--color-primary)]">{claim.order_context.asin}</span>
              )}
            </div>
          )}
        </div>

        {/* Right: financial + time */}
        <div className="flex flex-col items-end gap-1 min-w-[100px]">
          {canMoney && claim.order_context?.paid_amount && (
            <span className="text-sm font-medium text-[var(--color-text)]">{fmt(claim.order_context.paid_amount)}</span>
          )}
          {canMoney && claim.profit !== null && (
            <span className={`text-xs ${claim.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {t('ml.c.profit')}: {fmt(claim.profit)} ({claim.margin_pct}%)
            </span>
          )}
          <span className="text-xs text-[var(--color-text-muted)]">{timeAgo(claim.last_updated)}</span>
          {hasMandatory && dueDate && (
            <span className="text-xs text-red-400 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {new Date(dueDate).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Claim detail panel ──
function ClaimDetailPanel({
  claim, loading, sellerId, onClose, onRefresh, t
}: {
  claim: ClaimFullDetail | null;
  loading: boolean;
  sellerId: number;
  onClose: () => void;
  onRefresh: () => void;
  t: (k: string) => string;
}) {
  const can = useCan();
  const canMoney = can('global.view_money');
  const [msgText, setMsgText] = useState('');
  const [sending, setSending] = useState(false);
  const [actionLoading, setActionLoading] = useState('');
  const [expandedSection, setExpandedSection] = useState<string | null>('messages');
  const [invoiceSending, setInvoiceSending] = useState(false);
  const [invoiceStatus, setInvoiceStatus] = useState<'idle' | 'sent' | 'error'>('idle');

  // Return state
  const [returnData, setReturnData] = useState<any>(null);
  const [returnCost, setReturnCost] = useState<any>(null);
  const [returnReasons, setReturnReasons] = useState<any[]>([]);
  const [returnLoading, setReturnLoading] = useState(false);
  const [reviewMode, setReviewMode] = useState<'ok' | 'failed' | null>(null);
  const [reviewReason, setReviewReason] = useState('');
  const [reviewMessage, setReviewMessage] = useState('');
  const [reviewSubmitting, setReviewSubmitting] = useState(false);

  // Load return data when claim changes
  useEffect(() => {
    if (!claim) return;
    setReturnData(null);
    setReturnCost(null);
    setReturnReasons([]);
    setReviewMode(null);
    setReturnLoading(true);
    Promise.all([
      api.get(`/mercadolibre/claims/${claim.id}/returns?seller_id=${sellerId}`).catch(() => null),
      api.get(`/mercadolibre/claims/${claim.id}/return-cost?seller_id=${sellerId}`).catch(() => null),
    ]).then(([retRes, costRes]) => {
      if (retRes?.success && retRes.data) setReturnData(retRes.data);
      if (costRes?.success && costRes.data) setReturnCost(costRes.data);
      setReturnLoading(false);
    });
  }, [claim?.id, sellerId]);

  const loadReturnReasons = async () => {
    if (!claim || returnReasons.length > 0) return;
    const res = await api.get<any>(`/mercadolibre/claims/${claim.id}/return-reasons?seller_id=${sellerId}`);
    if (res.success && res.data) {
      const reasons = Array.isArray(res.data) ? res.data : res.data.reasons || res.data.data || [];
      setReturnReasons(reasons);
    }
  };

  const submitReturnReview = async (status: 'ok' | 'failed') => {
    if (!claim) return;
    const confirmMsg = status === 'ok' ? t('ml.c.confirmReviewOk') : t('ml.c.confirmReviewFailed');
    if (!window.confirm(confirmMsg)) return;

    setReviewSubmitting(true);
    const body: any = { status };
    if (status === 'failed') {
      body.reason = reviewReason;
      if (reviewMessage.trim()) body.message = reviewMessage;
    }
    const res = await api.post(`/mercadolibre/claims/${claim.id}/return-review?seller_id=${sellerId}`, body);
    if (res.success) {
      setReviewMode(null);
      onRefresh();
    } else {
      alert(t('ml.c.returnReviewError') + ': ' + (res as any).error);
    }
    setReviewSubmitting(false);
  };

  const sendInvoice = async () => {
    if (!claim) return;
    setInvoiceSending(true);
    setInvoiceStatus('idle');
    const res = await api.post(`/mercadolibre/claims/${claim.id}/send-invoice?seller_id=${sellerId}`, {});
    if (res.success) {
      setInvoiceStatus('sent');
      onRefresh();
    } else {
      setInvoiceStatus('error');
      alert('Error enviando invoice: ' + ((res as any).error || 'Unknown'));
    }
    setInvoiceSending(false);
  };

  const sellerPlayer = claim?.players?.find(p => p.role === 'respondent');
  const actions = sellerPlayer?.available_actions?.map(a => a.action) || [];

  // Además de lo que habilita ML, cada acción pide su capacidad del rol.
  const canSendToBuyer = actions.includes('send_message_to_complainant') && can('orders.messages');
  const canSendToMediator = actions.includes('send_message_to_mediator') && can('orders.claims_manage');
  const canRefund = actions.includes('refund') && can('ml.claims_refund');
  const canPartialRefund = actions.includes('allow_partial_refund') && can('ml.claims_refund');
  const canReturn = (actions.includes('allow_return') || actions.includes('allow_return_label')) && can('ml.claims_refund');
  const canDispute = actions.includes('open_dispute') && can('ml.claims_refund');

  const sendMessage = async (receiverRole: string) => {
    if (!msgText.trim() || !claim) return;
    setSending(true);
    const res = await api.post(`/mercadolibre/claims/${claim.id}/message?seller_id=${sellerId}`, {
      message: msgText,
      receiver_role: receiverRole,
    });
    if (res.success) {
      setMsgText('');
      onRefresh();
    } else {
      alert(t('ml.c.messageError') + ': ' + (res as any).error);
    }
    setSending(false);
  };

  const executeAction = async (action: string) => {
    if (!claim) return;
    const confirmMsg =
      action === 'refund' ? t('ml.c.confirmRefund') :
      action === 'dispute' ? t('ml.c.confirmDispute') :
      action === 'return' ? t('ml.c.confirmReturn') : '';
    if (confirmMsg && !window.confirm(confirmMsg)) return;

    setActionLoading(action);
    const res = await api.post(`/mercadolibre/claims/${claim.id}/${action}?seller_id=${sellerId}`, {});
    if (res.success) {
      onRefresh();
    } else {
      const errorKey = `ml.c.${action}Error`;
      alert(t(errorKey) + ': ' + (res as any).error);
    }
    setActionLoading('');
  };

  const toggleSection = (s: string) => setExpandedSection(expandedSection === s ? null : s);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-2xl h-full bg-[var(--color-bg)] border-l border-[var(--color-border)] overflow-y-auto shadow-2xl">
        {loading || !claim ? (
          <div className="p-8 text-center text-[var(--color-text-muted)]">{t('common.loading')}</div>
        ) : (
          <div className="p-4 space-y-4">
            {/* Bolsa WH — bloque destacado al inicio */}
            {claim.order_context?.ml_order_id && (
              <BagBadge
                mlOrderId={claim.order_context.ml_order_id}
                sellerId={sellerId}
                variant="full"
              />
            )}

            {/* Header */}
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold text-[var(--color-text)]">
                    {t('ml.c.claimDetail')} #{claim.id}
                  </span>
                  <span className="text-lg">{SITE_FLAGS[claim.site_id] || ''}</span>
                </div>
                <div className="text-sm text-[var(--color-text-muted)] mt-1">
                  {claim.detail?.title || claim.reason_id}
                </div>
              </div>
              <button onClick={onClose} className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text)]">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Status + Stage */}
            <div className="flex flex-wrap gap-2">
              <StatusBadge status={claim.status === 'opened' ? 'warning' : 'ok'} label={claim.status} size="sm" />
              <span className="text-xs px-2 py-1 rounded-full bg-blue-500/10 text-blue-400 border border-blue-500/20">
                {t(`ml.c.${claim.stage}`) || claim.stage}
              </span>
              <span className="text-xs px-2 py-1 rounded-full bg-purple-500/10 text-purple-400">
                {getReasonType(claim.reason_id)}
              </span>
              {claim.affects_reputation && (
                <span className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 ${
                  claim.affects_reputation.affects_reputation === 'affected'
                    ? 'bg-red-500/10 text-red-400' : 'bg-green-500/10 text-green-400'
                }`}>
                  <Shield className="w-3 h-3" />
                  {t('ml.c.affectsReputation')}: {claim.affects_reputation.affects_reputation === 'affected' ? 'Sí' : 'No'}
                </span>
              )}
            </div>

            {/* Detail info */}
            {claim.detail && (
              <div className="bg-[var(--color-bg-secondary)] rounded-lg p-3 border border-[var(--color-border)]">
                <div className="text-sm text-[var(--color-text)]">{claim.detail.problem}</div>
                <div className="text-xs text-[var(--color-text-muted)] mt-1">{claim.detail.description}</div>
                {claim.detail.due_date && (
                  <div className="flex items-center gap-1 mt-2 text-xs text-yellow-400">
                    <Clock className="w-3 h-3" />
                    {t('ml.c.dueDate')}: {new Date(claim.detail.due_date).toLocaleString()}
                  </div>
                )}
                <div className="text-xs text-[var(--color-text-muted)] mt-1">
                  {t('ml.c.responsible')}: {claim.detail.action_responsible}
                </div>
              </div>
            )}

            {/* Order context */}
            {claim.order_context && (
              <div className="bg-[var(--color-bg-secondary)] rounded-lg p-3 border border-[var(--color-border)]">
                <h4 className="text-sm font-medium text-[var(--color-text)] mb-2">{t('ml.c.product')}</h4>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-[var(--color-text-muted)]">{t('ml.c.buyer')}:</span>{' '}
                    <span className="text-[var(--color-text)]">{claim.order_context.buyer_nickname}</span>
                  </div>
                  <div>
                    <span className="text-[var(--color-text-muted)]">Order:</span>{' '}
                    <span className="font-mono text-[var(--color-text)]">#{claim.order_context.ml_order_id}</span>
                  </div>
                  {claim.order_context.item_title && (
                    <div className="col-span-2">
                      <span className="text-[var(--color-text-muted)]">{t('ml.c.product')}:</span>{' '}
                      <span className="text-[var(--color-text)]">{claim.order_context.item_title}</span>
                    </div>
                  )}
                  {claim.order_context.asin && (
                    <div>
                      <span className="text-[var(--color-text-muted)]">ASIN:</span>{' '}
                      <span className="font-mono text-[var(--color-primary)]">{claim.order_context.asin}</span>
                    </div>
                  )}
                  {canMoney && claim.order_context.paid_amount && (
                    <div>
                      <span className="text-[var(--color-text-muted)]">{t('common.total')}:</span>{' '}
                      <span className="text-[var(--color-text)] font-medium">{fmt(claim.order_context.paid_amount)}</span>
                    </div>
                  )}
                  {canMoney && claim.order_context.amazon_cost && (
                    <div>
                      <span className="text-[var(--color-text-muted)]">{t('ml.c.amazonCost')}:</span>{' '}
                      <span className="text-[var(--color-text)]">{fmt(claim.order_context.amazon_cost)}</span>
                    </div>
                  )}
                  {canMoney && claim.profit !== null && (
                    <div>
                      <span className="text-[var(--color-text-muted)]">{t('ml.c.profit')}:</span>{' '}
                      <span className={claim.profit >= 0 ? 'text-green-400' : 'text-red-400'}>
                        {fmt(claim.profit)} ({claim.margin_pct}%)
                      </span>
                    </div>
                  )}
                  {claim.order_context.shipment_tracking && (
                    <div>
                      <span className="text-[var(--color-text-muted)]">{t('ml.c.tracking')}:</span>{' '}
                      <span className="font-mono text-[var(--color-text)]">{claim.order_context.shipment_tracking}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Actions */}
            {claim.status === 'opened' && (canRefund || canPartialRefund || canReturn || canDispute) && (
              <div className="flex flex-wrap gap-2">
                {canRefund && (
                  <button
                    onClick={() => executeAction('refund')}
                    disabled={!!actionLoading}
                    className="px-3 py-1.5 bg-red-500/10 text-red-400 border border-red-500/20 rounded-lg text-xs hover:bg-red-500/20 disabled:opacity-50"
                  >
                    <DollarSign className="w-3 h-3 inline mr-1" />
                    {actionLoading === 'refund' ? '...' : t('ml.c.fullRefund')}
                  </button>
                )}
                {canPartialRefund && (
                  <button
                    onClick={() => executeAction('partial-refund')}
                    disabled={!!actionLoading}
                    className="px-3 py-1.5 bg-yellow-500/10 text-yellow-400 border border-yellow-500/20 rounded-lg text-xs hover:bg-yellow-500/20 disabled:opacity-50"
                  >
                    {actionLoading === 'partial-refund' ? '...' : t('ml.c.partialRefund')}
                  </button>
                )}
                {canReturn && (
                  <button
                    onClick={() => executeAction('return')}
                    disabled={!!actionLoading}
                    className="px-3 py-1.5 bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded-lg text-xs hover:bg-blue-500/20 disabled:opacity-50"
                  >
                    {actionLoading === 'return' ? '...' : t('ml.c.allowReturn')}
                  </button>
                )}
                {canDispute && (
                  <button
                    onClick={() => executeAction('dispute')}
                    disabled={!!actionLoading}
                    className="px-3 py-1.5 bg-purple-500/10 text-purple-400 border border-purple-500/20 rounded-lg text-xs hover:bg-purple-500/20 disabled:opacity-50"
                  >
                    {actionLoading === 'dispute' ? '...' : t('ml.c.openDispute')}
                  </button>
                )}
              </div>
            )}

            {/* Return section — only shown when return data exists */}
            {(returnData || returnLoading) && (
              <CollapsibleSection
                title={t('ml.c.returnSection')}
                icon={<Package className="w-4 h-4" />}
                expanded={expandedSection === 'return'}
                onToggle={() => toggleSection('return')}
              >
                {returnLoading ? (
                  <div className="text-xs text-[var(--color-text-muted)]">{t('common.loading')}</div>
                ) : returnData ? (
                  <div className="space-y-3">
                    {/* Return status info */}
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-[var(--color-text-muted)]">{t('ml.c.returnStatus')}:</span>{' '}
                        <span className={`font-medium ${
                          returnData.status === 'opened' ? 'text-yellow-400' :
                          returnData.status === 'shipped' ? 'text-blue-400' :
                          returnData.status === 'delivered' ? 'text-green-400' :
                          returnData.status === 'cancelled' ? 'text-red-400' :
                          'text-[var(--color-text)]'
                        }`}>
                          {returnData.status}
                        </span>
                      </div>
                      {returnData.shipping_status && (
                        <div>
                          <span className="text-[var(--color-text-muted)]">{t('ml.c.returnShipping')}:</span>{' '}
                          <span className={`font-medium ${
                            returnData.shipping_status === 'shipped' ? 'text-blue-400' :
                            returnData.shipping_status === 'delivered' ? 'text-green-400' :
                            'text-[var(--color-text)]'
                          }`}>
                            {returnData.shipping_status}
                          </span>
                        </div>
                      )}
                      {returnData.tracking_number && (
                        <div>
                          <span className="text-[var(--color-text-muted)]">{t('ml.c.tracking')}:</span>{' '}
                          <span className="font-mono text-[var(--color-text)]">{returnData.tracking_number}</span>
                        </div>
                      )}
                      {returnData.refund_at && (
                        <div>
                          <span className="text-[var(--color-text-muted)]">Refund at:</span>{' '}
                          <span className="text-[var(--color-text)]">{new Date(returnData.refund_at).toLocaleString()}</span>
                        </div>
                      )}
                      {returnData.warehouse_review && (
                        <div>
                          <span className="text-[var(--color-text-muted)]">Warehouse review:</span>{' '}
                          <span className="text-[var(--color-text)]">{returnData.warehouse_review.status || JSON.stringify(returnData.warehouse_review)}</span>
                        </div>
                      )}
                      {returnData.seller_review && (
                        <div>
                          <span className="text-[var(--color-text-muted)]">Seller review:</span>{' '}
                          <span className="text-[var(--color-text)]">{returnData.seller_review.status || JSON.stringify(returnData.seller_review)}</span>
                        </div>
                      )}
                      {returnData.date_created && (
                        <div>
                          <span className="text-[var(--color-text-muted)]">{t('common.date')}:</span>{' '}
                          <span className="text-[var(--color-text)]">{new Date(returnData.date_created).toLocaleString()}</span>
                        </div>
                      )}
                    </div>

                    {/* Return cost */}
                    {canMoney && returnCost && (
                      <div className="text-xs">
                        <span className="text-[var(--color-text-muted)]">{t('ml.c.returnCost')}:</span>{' '}
                        <span className="text-[var(--color-text)] font-medium">
                          {returnCost.amount_usd ? fmt(returnCost.amount_usd) : returnCost.amount ? `${returnCost.currency_id || ''} ${returnCost.amount}` : JSON.stringify(returnCost)}
                        </span>
                      </div>
                    )}

                    {/* Review actions */}
                    {claim.status === 'opened' && !reviewMode && can('orders.claims_manage') && (
                      <div className="flex gap-2 pt-2 border-t border-[var(--color-border)]">
                        <button
                          onClick={() => submitReturnReview('ok')}
                          disabled={reviewSubmitting}
                          className="px-3 py-1.5 bg-green-500/10 text-green-400 border border-green-500/20 rounded-lg text-xs hover:bg-green-500/20 disabled:opacity-50"
                        >
                          {t('ml.c.reviewOk')}
                        </button>
                        <button
                          onClick={() => { setReviewMode('failed'); loadReturnReasons(); }}
                          disabled={reviewSubmitting}
                          className="px-3 py-1.5 bg-red-500/10 text-red-400 border border-red-500/20 rounded-lg text-xs hover:bg-red-500/20 disabled:opacity-50"
                        >
                          {t('ml.c.reviewFailed')}
                        </button>
                      </div>
                    )}

                    {/* Failed review form */}
                    {reviewMode === 'failed' && can('orders.claims_manage') && (
                      <div className="space-y-2 pt-2 border-t border-[var(--color-border)]">
                        <label className="text-xs text-[var(--color-text-muted)]">{t('ml.c.reviewReason')}</label>
                        <select
                          value={reviewReason}
                          onChange={(e) => setReviewReason(e.target.value)}
                          className="w-full p-2 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
                        >
                          <option value="">-- {t('ml.c.reviewReason')} --</option>
                          {returnReasons.map((r: any, i: number) => (
                            <option key={i} value={r.id || r.code || r.value || r}>
                              {r.description || r.label || r.name || r.id || String(r)}
                            </option>
                          ))}
                        </select>
                        <label className="text-xs text-[var(--color-text-muted)]">{t('ml.c.reviewMessage')}</label>
                        <textarea
                          value={reviewMessage}
                          onChange={(e) => setReviewMessage(e.target.value)}
                          placeholder={t('ml.c.reviewMessage')}
                          className="w-full p-2 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)] resize-none"
                          rows={2}
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => submitReturnReview('failed')}
                            disabled={reviewSubmitting || !reviewReason}
                            className="px-3 py-1.5 bg-red-500/10 text-red-400 border border-red-500/20 rounded-lg text-xs hover:bg-red-500/20 disabled:opacity-50"
                          >
                            {reviewSubmitting ? '...' : t('ml.c.submitReview')}
                          </button>
                          <button
                            onClick={() => setReviewMode(null)}
                            className="px-3 py-1.5 text-[var(--color-text-muted)] text-xs hover:text-[var(--color-text)]"
                          >
                            {t('common.cancel')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-xs text-[var(--color-text-muted)]">{t('ml.c.noReturn')}</div>
                )}
              </CollapsibleSection>
            )}

            {/* Messages section */}
            <CollapsibleSection
              title={`${t('ml.c.messages')} (${claim.messages?.length || 0})`}
              icon={<MessageCircle className="w-4 h-4" />}
              expanded={expandedSection === 'messages'}
              onToggle={() => toggleSection('messages')}
            >
              <div className="space-y-3">
                {claim.messages?.map((msg, i) => {
                  const rawMsg = isHtml(msg.message) ? stripHtml(msg.message) : msg.message;
                  const rawTranslated = msg.translated_message
                    ? (isHtml(msg.translated_message) ? stripHtml(msg.translated_message) : msg.translated_message)
                    : null;
                  return (
                    <div key={i} className={`p-3 rounded-lg text-sm ${
                      msg.sender_role === 'respondent'
                        ? 'bg-blue-500/10 border border-blue-500/20 ml-4'
                        : msg.sender_role === 'mediator'
                        ? 'bg-purple-500/10 border border-purple-500/20'
                        : 'bg-[var(--color-bg)] border border-[var(--color-border)] mr-4'
                    }`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium text-[var(--color-text-muted)]">
                          {msg.sender_role === 'respondent' ? 'Vendedor' : msg.sender_role === 'complainant' ? 'Comprador' : 'Mediador'}
                        </span>
                        <span className="text-xs text-[var(--color-text-muted)]">
                          {new Date(msg.date_created).toLocaleString()}
                        </span>
                      </div>
                      <div className="text-[var(--color-text)] whitespace-pre-line">{rawMsg}</div>
                      {rawTranslated && rawTranslated !== rawMsg && (
                        <details className="mt-2">
                          <summary className="text-xs text-[var(--color-text-muted)] cursor-pointer hover:text-[var(--color-primary)]">
                            {t('ml.c.showTranslation')}
                          </summary>
                          <div className="text-xs text-[var(--color-text-muted)] mt-1 italic whitespace-pre-line">{rawTranslated}</div>
                        </details>
                      )}
                    </div>
                  );
                })}

                {/* Send message */}
                {claim.status === 'opened' && (canSendToBuyer || canSendToMediator) && (
                  <div className="space-y-2">
                    <textarea
                      value={msgText}
                      onChange={(e) => setMsgText(e.target.value)}
                      placeholder={t('ml.c.messagePlaceholder')}
                      className="w-full p-3 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)] resize-none"
                      rows={3}
                    />
                    <div className="flex gap-2">
                      {canSendToBuyer && (
                        <button
                          onClick={() => sendMessage('complainant')}
                          disabled={sending || !msgText.trim()}
                          className="flex items-center gap-1 px-3 py-1.5 bg-[var(--color-primary)] text-white rounded-lg text-xs hover:opacity-90 disabled:opacity-50"
                        >
                          <Send className="w-3 h-3" />
                          {t('ml.c.sendToBuyer')}
                        </button>
                      )}
                      {canSendToMediator && (
                        <button
                          onClick={() => sendMessage('mediator')}
                          disabled={sending || !msgText.trim()}
                          className="flex items-center gap-1 px-3 py-1.5 bg-purple-500/20 text-purple-400 border border-purple-500/30 rounded-lg text-xs hover:bg-purple-500/30 disabled:opacity-50"
                        >
                          <Send className="w-3 h-3" />
                          {t('ml.c.sendToMediator')}
                        </button>
                      )}
                      {can('orders.claims_manage') && (
                      <button
                        onClick={sendInvoice}
                        disabled={invoiceSending || invoiceStatus === 'sent'}
                        className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium ml-auto ${
                          invoiceStatus === 'sent'
                            ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                            : 'bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30'
                        } disabled:opacity-50`}
                      >
                        {invoiceSending ? (
                          <><RefreshCw className="w-3 h-3 animate-spin" /> Enviando...</>
                        ) : invoiceStatus === 'sent' ? (
                          <><DollarSign className="w-3 h-3" /> Invoice Enviado</>
                        ) : (
                          <><DollarSign className="w-3 h-3" /> Send Invoice</>
                        )}
                      </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </CollapsibleSection>

            {/* Status history */}
            <CollapsibleSection
              title={t('ml.c.statusHistory')}
              icon={<Clock className="w-4 h-4" />}
              expanded={expandedSection === 'status'}
              onToggle={() => toggleSection('status')}
            >
              <div className="space-y-2">
                {claim.status_history?.map((sh, i) => (
                  <div key={i} className="flex items-center gap-3 text-xs">
                    <span className="text-[var(--color-text-muted)] min-w-[120px]">
                      {new Date(sh.date).toLocaleString()}
                    </span>
                    <StatusBadge
                      status={sh.status === 'opened' ? 'warning' : 'ok'}
                      label={`${sh.stage} / ${sh.status}`}
                      size="sm"
                    />
                    <span className="text-[var(--color-text-muted)]">by {sh.change_by}</span>
                  </div>
                ))}
              </div>
            </CollapsibleSection>

            {/* Actions history */}
            <CollapsibleSection
              title={t('ml.c.actionsHistory')}
              icon={<ArrowUpRight className="w-4 h-4" />}
              expanded={expandedSection === 'actions'}
              onToggle={() => toggleSection('actions')}
            >
              <div className="space-y-2">
                {claim.actions_history?.map((ah: any, i: number) => (
                  <div key={i} className="flex items-center gap-3 text-xs">
                    <span className="text-[var(--color-text-muted)] min-w-[120px]">
                      {new Date(ah.date_created).toLocaleString()}
                    </span>
                    <span className="text-[var(--color-text)] font-mono">{ah.action_name}</span>
                    <span className="text-[var(--color-text-muted)]">by {ah.player_role}</span>
                  </div>
                ))}
              </div>
            </CollapsibleSection>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Collapsible section ──
function CollapsibleSection({
  title, icon, expanded, onToggle, children
}: {
  title: string;
  icon: React.ReactNode;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2 p-3 text-sm font-medium text-[var(--color-text)] hover:bg-[var(--color-bg)] transition-colors"
      >
        {icon}
        {title}
        <span className="ml-auto">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </span>
      </button>
      {expanded && <div className="p-3 pt-0">{children}</div>}
    </div>
  );
}
